import { SlideHeader } from "../../../SlideHeader";

const APTONOMY_DOES: Array<{ label: string; detail: string }> = [
  {
    label: "Unified audit across iOS + Android",
    detail:
      "One framework, one rubric, one lift-attribution methodology — applied across both stores in a single report.",
  },
  {
    label: "Cross-platform competitive context",
    detail:
      "Competitors on Android shape what iOS publishers should do, and vice versa. Apptonomy sees both surfaces; native tools see one.",
  },
  {
    label: "Bi-platform publish in one action",
    detail:
      "Assets, copy, and localization pushed to both App Store Connect and Google Play Console from one workflow.",
  },
  {
    label: "Surface-neutral methodology",
    detail:
      "AI-discovery, sentiment, localization analysis — the same methodology applies regardless of which store an app lives in.",
  },
  {
    label: "Bi-platform attribution",
    detail:
      "Lift measurement across iOS + Android revisions, attributed back to recommendations across both surfaces.",
  },
];

const CAVEATS: string[] = [
  "Long-term defense framing, not a present-day moat. Native AI ASO from Apple or Google is plausible but not happening at threatening scale today.",
  "Argument weakens for customer cohorts that are heavily single-platform — verify cohort mix before leaning on it.",
  "If both Apple and Google ship excellent native AI ASO, the bar Apptonomy must clear rises: cross-platform unification has to be visibly better than two separate native experiences.",
];

export default function PlatformNative() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix H · Platform-Native Defense">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          What if Apple or Google ships{" "}
          <span className="text-blue-700">native ASO AI?</span>
        </h1>
        <p className="mt-3 text-xl font-medium italic tracking-tight text-slate-700 md:mt-4 md:text-2xl">
          The most plausible long-term competitive threat is platform-native, not another startup. Apptonomy survives by structure.
        </p>
      </SlideHeader>

      {/* The structural argument */}
      <div className="rounded-xl bg-blue-50 p-4 ring-1 ring-blue-200 md:p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          The structural argument
        </p>
        <p className="mt-2 text-sm leading-snug text-slate-800 md:text-base md:leading-snug">
          Apple ships features for{" "}
          <span className="font-semibold text-slate-900">iOS apps in the App Store</span>.
          Google ships features for{" "}
          <span className="font-semibold text-slate-900">Android apps in Google Play</span>.
          Neither has ever shipped a tool that helps publishers manage the other platform — and they never will.
        </p>
        <p className="mt-2 text-sm leading-snug text-slate-800 md:text-base md:leading-snug">
          Any publisher with apps on both stores needs a tool that lives{" "}
          <span className="font-semibold text-slate-900">above both</span>. That's where Apptonomy lives.
        </p>
      </div>

      {/* What Apptonomy does + Caveats — two columns */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:mt-5 md:grid-cols-[3fr_2fr] md:gap-6">
        {/* Concrete things Apptonomy does */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:mb-3 md:text-sm">
            Concrete things Apptonomy does that platform-native AI never will
          </p>
          <ul className="space-y-1.5 md:space-y-2">
            {APTONOMY_DOES.map((item) => (
              <li
                key={item.label}
                className="rounded-lg bg-white p-2.5 ring-1 ring-slate-200 md:p-3"
              >
                <p className="text-sm font-semibold leading-tight text-slate-900 md:text-base">
                  {item.label}
                </p>
                <p className="mt-0.5 text-xs leading-snug text-slate-600 md:text-sm">
                  {item.detail}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Caveats */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 md:mb-3 md:text-sm">
            Honest caveats
          </p>
          <ul className="space-y-1.5 md:space-y-2">
            {CAVEATS.map((caveat) => (
              <li
                key={caveat}
                className="rounded-lg bg-slate-50 p-2.5 ring-1 ring-slate-200 md:p-3"
              >
                <p className="text-xs leading-snug text-slate-700 md:text-sm">
                  {caveat}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
